import { use, useCallback } from "react";
import { useI18n } from "../../hooks/i18n-hooks";
import { useMutation } from "@apollo/client/react";
import { AuthManagerContext } from "../../contexts/AuthManager";
import VStack from "../../ui_atoms/VStack";
import ScreenCard from "../ScreenCard";
import { useApolloErrorSnackbar, useSnackbar } from "../../hooks/snackbar";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { useCloseEditor } from "../profile-editor/use-close-editor";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { UPDATE_USER } from "../../graphql/identity";
import { UPDATE_CLIENT_PROFILE } from "../../graphql/core";
import { RESEND_VERIFICATION_EMAIL } from "../../graphql/home";
import isEmail from "validator/lib/isEmail";
import TextField from "../../components/TextField";
import { usePostOnboardingData } from "../../hooks/use-postonboarding-data";
import { useCurrentProfile } from "../../hooks/use-current-profile";
import { ClientProfile } from "../../__generated__/graphql";
import { useIntercom } from "../../hooks/intercom";
import { BodyMediumText } from "../../components/FormattedText";
import Label from "../../ui_atoms/Label";
import ListItem from "../../ui_atoms/ListItem";
import { useTheme } from "../../hooks/use-theme";

type FormData = {
    email?: string;
};

type EmailEditorScreenProps = StackScreenProps<RootStackParamList, "EmailEdit">;

const EmailEditorScreen = ({ navigation, route }: EmailEditorScreenProps) => {
    // `businessEmail` mode edits the company employer's business email (a distinct field on the
    // client profile); the default mode edits the user's personal email. Private (domestic)
    // employers reuse their personal email, so they go through the default mode too.
    // `lockWhenVerified` greys the field once verified — only the post-onboarding checklist wants
    // that; the employer editors (the sole way to change these emails) stay editable.
    const { profileId, businessEmail: businessEmailMode, lockWhenVerified } = route.params || {};
    const { email: userEmail, emailVerified: userEmailVerified } = usePostOnboardingData();
    const clientProfile = useCurrentProfile<ClientProfile>();
    const { refreshUser } = use(AuthManagerContext);
    const { updateUser: updateIntercomUser } = useIntercom();
    const { gridSize } = useTheme();
    const { showSnackbar } = useSnackbar();

    const email = businessEmailMode ? clientProfile?.businessEmail : userEmail;
    const emailVerified = businessEmailMode
        ? clientProfile?.businessEmailVerified
        : userEmailVerified;

    const [updateUser, { error: saveUserError, loading: savingUser, reset: resetUser }] =
        useMutation(UPDATE_USER, profileId ? { context: { liftProfile: profileId } } : undefined);
    const [
        updateClientProfile,
        { error: saveClientError, loading: savingClient, reset: resetClient },
    ] = useMutation(UPDATE_CLIENT_PROFILE);
    const [
        resendVerificationEmail,
        { error: resendError, loading: resending, reset: resetResend },
    ] = useMutation(RESEND_VERIFICATION_EMAIL);

    const saving = businessEmailMode ? savingClient : savingUser;

    useApolloErrorSnackbar(
        businessEmailMode ? saveClientError : saveUserError,
        businessEmailMode ? resetClient : resetUser,
    );
    useApolloErrorSnackbar(resendError, resetResend);

    const i18n = useI18n();

    const {
        handleSubmit,
        control,
        formState: { isValid, isDirty },
    } = useForm<FormData>({
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues: {
            email: email || undefined,
        },
    });

    const onSubmit = useCallback<SubmitHandler<FormData>>(
        async (data) => {
            try {
                if (businessEmailMode) {
                    await updateClientProfile({
                        variables: { changes: { businessEmail: data.email } },
                    });
                } else {
                    await updateUser({ variables: { userData: { email: data.email } } });
                    // We update the intercom user to use the private email
                    void updateIntercomUser({ email: data.email });
                }
                navigation.goBack();
                void refreshUser?.();
            } finally {
                // this is handled by the mutation error snackbar
            }
        },
        [
            businessEmailMode,
            updateClientProfile,
            updateUser,
            updateIntercomUser,
            navigation,
            refreshUser,
        ],
    );

    const onResend = useCallback(async () => {
        try {
            await resendVerificationEmail({ variables: { clientProfile: !!businessEmailMode } });
            showSnackbar({
                mode: "success",
                text: i18n.t("screens.profile_editor.email.resend_sent", { email }),
            });
        } catch (_e) {
            // this is handled by resendError in useMutation
        }
    }, [resendVerificationEmail, businessEmailMode, showSnackbar, i18n, email]);

    useCloseEditor(isDirty && !saving);

    return (
        <ScreenCard
            buttons={[
                {
                    title: i18n.t("save"),
                    onPress: handleSubmit(onSubmit),
                    validating: saving,
                    isValid: isValid && isDirty,
                    mode: "secondary",
                },
            ]}
            withAssistanceButton
        >
            <VStack gap={0.5} alignment="stretch" h="2/3">
                <BodyMediumText>
                    {i18n.t(
                        emailVerified
                            ? lockWhenVerified
                                ? "screens.profile_editor.email.verified_description_postonboarding"
                                : "screens.profile_editor.email.verified_description"
                            : "screens.profile_editor.email.not_verified_description",
                    )}
                </BodyMediumText>
                <VStack gap={0.25}>
                    <Controller
                        control={control}
                        name="email"
                        rules={{
                            validate: (v = "") => isEmail(v),
                        }}
                        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                            <TextField
                                label={i18n.t(
                                    businessEmailMode
                                        ? "user.business_email_info"
                                        : "screens.profile_editor.email.title",
                                )}
                                value={value}
                                onChangeText={onChange}
                                onBlur={onBlur}
                                error={error?.message}
                                disabled={!!lockWhenVerified && !!emailVerified}
                                keyboardType="email-address"
                                textContentType="emailAddress"
                                autoComplete="email"
                                autoCorrect={false}
                                autoCapitalize="none"
                                right={
                                    // Only while the field still holds the verified address;
                                    // editing it drops the badge.
                                    emailVerified && value === email ? (
                                        <Label
                                            text={i18n.t(
                                                "screens.profile_editor.email.verified_badge",
                                            )}
                                            mode="success"
                                        />
                                    ) : undefined
                                }
                            />
                        )}
                    />
                </VStack>
                {!emailVerified && (
                    <ListItem
                        style={{ marginHorizontal: -(gridSize / 1.5) }}
                        onPress={onResend}
                        disabled={resending}
                        labels={[
                            {
                                text: i18n.t("screens.profile_editor.email.resend"),
                                mode: "cta",
                            },
                        ]}
                        rightIcon="send"
                        rightActionMode="action_simple"
                    />
                )}
            </VStack>
        </ScreenCard>
    );
};

export default EmailEditorScreen;
